/**
 * Persistence, as an interface core declares and each platform implements.
 *
 * IndexedDB on web today, SQLite on native later (ADR-0002). This is the seam
 * that makes the mobile app a re-implementation rather than a rewrite, so it is
 * kept deliberately narrow: every method here has to be implemented twice, and
 * convenience methods that are really composed queries belong in the repository
 * layer above rather than in each adapter.
 */

import type { Entry, Tag } from '../model/entry.js'
import type { CheckInTemplate } from '../model/template.js'
import type { Track } from '../model/track.js'

/** An instant range, inclusive at both ends. */
export interface EpochRange {
  readonly fromEpochMs: number
  readonly toEpochMs: number
}

export interface Collection<T> {
  get(id: string): Promise<T | undefined>
  list(): Promise<T[]>
  put(item: T): Promise<void>
  putMany(items: readonly T[]): Promise<void>
  remove(id: string): Promise<void>
  count(): Promise<number>
  /**
   * Empties the collection.
   *
   * Here rather than composed from `list()` and `remove()` because both engines
   * do it natively in one operation, and because its only caller is a replace
   * import (T-502) — which runs inside a transaction that must be able to
   * restore a database of any size without issuing one delete per row.
   */
  clear(): Promise<void>
}

export interface EntryCollection extends Collection<Entry> {
  /** Ascending by instant. Inclusive of both bounds. */
  listByRange(range: EpochRange): Promise<Entry[]>
  /** Entries containing a measurement for this track. */
  listByTrack(trackId: string, range?: EpochRange): Promise<Entry[]>
  listByTag(tagId: string, range?: EpochRange): Promise<Entry[]>
  listByTemplate(templateId: string, range?: EpochRange): Promise<Entry[]>
  /**
   * The earliest and latest entry, or `undefined` on an empty database.
   *
   * Here rather than composed from `list()` in the repository, because both
   * platforms can answer it from an index in constant time and neither should
   * read every row to do it. "All time" is the app's default range and is
   * resolved on every screen, so this runs on every page load — at ten thousand
   * entries the composed version reads ten thousand rows each time, which is
   * both slow and, on a phone, several megabytes of garbage per navigation.
   */
  earliest(): Promise<Entry | undefined>
  latest(): Promise<Entry | undefined>
}

/**
 * Small persisted app state: the first-run marker, the last backup time, and
 * later the reminder schedules.
 *
 * Values are strings so this stays trivial to implement on both IndexedDB and
 * SQLite; callers encode anything structured themselves. The port is otherwise
 * kept narrow, but three separate tickets need persisted state that is not a
 * domain entity, and inventing a table per need would be worse.
 */
export interface SettingsStore {
  get(key: string): Promise<string | undefined>
  set(key: string, value: string): Promise<void>
  remove(key: string): Promise<void>
  all(): Promise<Record<string, string>>
}

export interface Storage {
  readonly tracks: Collection<Track>
  readonly tags: Collection<Tag>
  readonly templates: Collection<CheckInTemplate>
  readonly entries: EntryCollection
  readonly settings: SettingsStore

  /**
   * Runs `work` so that either all of its writes land or none do.
   *
   * Import (T-502) depends on this: restoring a backup must not be able to
   * leave a half-replaced database behind.
   */
  transaction<T>(work: (storage: Storage) => Promise<T>): Promise<T>

  getSchemaVersion(): Promise<number>
  setSchemaVersion(version: number): Promise<void>
}

// ─── Failures ─────────────────────────────────────────────────────────────────
//
// These are the one place this codebase throws rather than returning a value,
// and the exception is deliberate. Domain errors are values because they are
// *expected outcomes* the UI renders — a name that is too long is an answer to
// "is this valid?". The failures below are not answers to anything: the device
// is full, or the database was written by a build newer than this one. There is
// no sensible value to return from `get()` in either case, and the port's
// methods return `Promise<T>` rather than `Promise<Result<T>>` precisely
// because reads have no domain-level failure mode.
//
// They are declared here rather than in the web adapter because they are part
// of the port contract. The SQLite implementation hits the same two conditions
// and must report them the same way, or every screen that handles them has to
// be rewritten for native.

/** Base class, so a caller can catch every storage failure in one place. */
export class StorageError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options)
    this.name = new.target.name
  }
}

/**
 * The device is out of space for this origin.
 *
 * Recoverable and worth surfacing plainly: the user can export a backup and
 * clear old data (T-502), and on web an installed app gets a larger quota
 * (T-204). An unhandled rejection here presents as a check-in that silently
 * did not save, which is the worst possible failure for an app whose purpose
 * is producing evidence.
 */
export class StorageQuotaExceededError extends StorageError {}

/**
 * The stored database was written by a newer schema version than this build.
 *
 * Reading it partially is worse than not reading it at all — a stale cached
 * build would drop fields it does not know about and write the result back,
 * silently destroying data a newer build recorded. Fail closed instead.
 */
export class StorageSchemaTooNewError extends StorageError {
  constructor(
    readonly foundVersion: number,
    readonly supportedVersion: number,
  ) {
    super(
      `This data was saved by a newer version of the app (database version ${String(foundVersion)}, ` +
        `this build supports ${String(supportedVersion)}). Reload to update — nothing has been changed.`,
    )
  }
}
